import { lazy } from 'react';

const CHUNK_RELOAD_KEY = 'axion_chunk_reload_count';
export const MAX_CHUNK_RELOADS = 3;

function isChunkError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    msg.includes('ChunkLoadError') ||
    msg.includes('Loading chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('dynamically imported module')
  );
}

export function getChunkReloadCount(): number {
  try {
    return parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? '0', 10) || 0;
  } catch {
    return 0;
  }
}

/**
 * Monotonically increasing per-tab counter of reloads already spent on a
 * persistent chunk-load failure. It is NOT auto-reset on a successful load —
 * the only way it resets is the user explicitly retrying from the error screen
 * (see clearChunkReloadCount). This guarantees the page can never loop forever.
 */
export function incrementChunkReloadCount(): number {
  const next = getChunkReloadCount() + 1;
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(next));
  } catch {
    /* ignore quota / availability errors */
  }
  return next;
}

export function clearChunkReloadCount(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    /* ignore */
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function lazyRetry<T extends React.ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3,
  baseDelay = 500,
): React.LazyExoticComponent<T> {
  let attemptCount = 0;

  const retryImport = async (): Promise<{ default: T }> => {
    while (attemptCount < retries) {
      try {
        return await importFn();
      } catch (error: unknown) {
        attemptCount++;
        if (!isChunkError(error)) {
          throw error;
        }
        if (attemptCount < retries) {
          await sleep(baseDelay * Math.pow(2, attemptCount - 1));
        } else {
          // Auto-reload budget is managed by the surrounding
          // ChunkErrorBoundary (single choke point for every chunk failure).
          throw error;
        }
      }
    }
    throw new Error('unreachable');
  };

  return lazy(retryImport);
}